import type { RoiId } from '../../changes/ids';
import { DicomRoi } from '../../dicom/dicomRoi';
import { DicomStructureSet } from '../../dicom/dicomstructureset';

/** Why a copy into an existing structure set leaves a source structure out. */
export type CopySkipReason =
    | { kind: 'name-taken' }
    | { kind: 'ambiguous'; matchCount: number }
    | { kind: 'approved'; targetName: string }
    | { kind: 'locked'; targetName: string }
    | { kind: 'repeated-in-selection' };

/**
 * What copying one source structure into an existing structure set does. An
 * overwrite names the target structure by id, so the copy replaces the
 * structure the plan was made for or, when that one is gone, none.
 */
export type CopyTargetPlan =
    | { kind: 'create' }
    | { kind: 'overwrite'; targetRoiId: RoiId }
    | { kind: 'skip'; reason: CopySkipReason };

/** Target structures by their name as the copy compares names. */
export type TargetNameIndex = ReadonlyMap<string, readonly DicomRoi[]>;

/** Indexes `targetRois` by their name as the copy compares names. */
export function indexTargetNames(
    targetRois: Iterable<DicomRoi>
): TargetNameIndex {
    const index = new Map<string, DicomRoi[]>();
    for (const roi of targetRois) {
        const key = DicomStructureSet.trimmedNameKey(roi.name);
        const matches = index.get(key);
        if (matches) {
            matches.push(roi);
        } else {
            index.set(key, [roi]);
        }
    }
    return index;
}

/**
 * What copying a structure named `name` does to the target, on its own.
 *
 * A new name is created. A taken name is skipped unless overwriting, and an
 * overwrite needs exactly one target structure that accepts contour edits:
 * with several, the name does not say which one the user means, and an
 * approved or locked one is never overwritten (SR-WOR-H-42-1).
 */
export function planCopyTarget(
    name: string,
    targetIndex: TargetNameIndex,
    overwriteExisting: boolean
): CopyTargetPlan {
    const matches =
        targetIndex.get(DicomStructureSet.trimmedNameKey(name)) ?? [];
    const [target] = matches;
    if (!target) {
        return { kind: 'create' };
    }
    if (!overwriteExisting) {
        return { kind: 'skip', reason: { kind: 'name-taken' } };
    }
    if (matches.length > 1) {
        return {
            kind: 'skip',
            reason: { kind: 'ambiguous', matchCount: matches.length },
        };
    }
    switch (target.contourEditBlocker) {
        case 'approval':
            return {
                kind: 'skip',
                reason: { kind: 'approved', targetName: target.name },
            };
        case 'lock':
            return {
                kind: 'skip',
                reason: { kind: 'locked', targetName: target.name },
            };
        case undefined:
            return { kind: 'overwrite', targetRoiId: target.uid };
    }
}

/**
 * What a copy of `sources`, in the order given, does to the target. Every
 * plan is decided against the target as it was before the copy, so one
 * source never lands on a structure another source of the same copy made:
 * of several sources that share a name, only the first is copied. When the
 * first is skipped, the others are skipped for the same reason.
 */
export function planCopyTargets(
    sources: Iterable<{ roiId: RoiId; name: string }>,
    targetRois: Iterable<DicomRoi>,
    overwriteExisting: boolean
): Map<RoiId, CopyTargetPlan> {
    const targetIndex = indexTargetNames(targetRois);
    const firstPlanByName = new Map<string, CopyTargetPlan>();
    const plans = new Map<RoiId, CopyTargetPlan>();
    for (const source of sources) {
        const key = DicomStructureSet.trimmedNameKey(source.name);
        const firstPlan = firstPlanByName.get(key);
        if (firstPlan) {
            plans.set(source.roiId, planRepeatedName(firstPlan));
            continue;
        }
        const plan = planCopyTarget(
            source.name,
            targetIndex,
            overwriteExisting
        );
        firstPlanByName.set(key, plan);
        plans.set(source.roiId, plan);
    }
    return plans;
}

/** The plan for a later source with the name `firstPlan` was made for. */
function planRepeatedName(firstPlan: CopyTargetPlan): CopyTargetPlan {
    switch (firstPlan.kind) {
        case 'create':
        case 'overwrite':
            return { kind: 'skip', reason: { kind: 'repeated-in-selection' } };
        case 'skip':
            return firstPlan;
    }
}
